import {
	Paper,
	Typography,
	TextField,
	Button,
	Box,
	CircularProgress,
	Chip,
	Alert,
	Link,
} from '@mui/material'
import React, { memo, useCallback, useState, useEffect } from 'react'
import { SurveyBacklogList } from './WorkItemHierarchy/SurveyBacklogList'
import { ComponentChangeSummary, SurveyDetails } from '../../types/Work/Survey'
import { useTokenClaims } from '../../security/useTokenClaims'
import { getSurveyDetails, completeSurvey, getMissingWorkItems } from '../../services/quantitySurveyService'
import { extractComponentChangeSummaries } from '../../utils/WorkItemsTreeUtils'
import AssignmentIcon from '@mui/icons-material/Assignment'

// Memoize the SurveyBacklogList component
const MemoizedSurveyBacklogList = memo(SurveyBacklogList)

interface TakeOffSheetProps {
	selectedSurveyId: number | null
	loadData: (showLoading?: boolean) => Promise<void>
	setSearchWorkItem: (workItemName: string) => void
}

export default function TakeOffSheet({ selectedSurveyId, loadData, setSearchWorkItem }: TakeOffSheetProps) {
	const { role } = useTokenClaims()

	const [selectedSurveyDetails, setSelectedSurveyDetails] = useState<SurveyDetails | null>(null)
	const [sheetLoading, setSheetLoading] = useState(false)
	const [surveyCompleted, setSurveyCompleted] = useState(false)
	const [surveyComment, setSurveyComment] = React.useState('')
	const [submiting, setSubmiting] = React.useState(false)
	const [surveyCommentError, setSurveyCommentError] = React.useState('')
	const [componentChangeSummaries, setComponentChangeSummaries] = useState<
		ComponentChangeSummary[]
	>([])
	const [missingAlert, setMissingAlert] = useState<{
		workItemType: string
		splittedRequests: number
		workItemName: string
	} | null>(null)

	useEffect(() => {
		if (selectedSurveyId) {
			setSheetLoading(true)
			fetchSurveyDetail().finally(() => setSheetLoading(false))
		} else {
			setSelectedSurveyDetails(null)
			setSurveyCompleted(false)
		}
	}, [selectedSurveyId])

	const fetchSurveyDetail = useCallback(async () => {
		if (selectedSurveyId) {
			const [surveyDetails, missingAlert] = await Promise.all([getSurveyDetails(selectedSurveyId), getMissingWorkItems(selectedSurveyId)])
			setSelectedSurveyDetails(surveyDetails)
			setMissingAlert(missingAlert)
			setSurveyCompleted(surveyDetails.status === 'Surveyed')
			setSurveyComment(surveyDetails.surveyComment ?? '')
			setComponentChangeSummaries(extractComponentChangeSummaries(surveyDetails.surveyWorkItem))
		}
	}, [selectedSurveyId])

	const handleSubmit = async (isMoreInfoRequired: boolean) => {
		if (isMoreInfoRequired && surveyComment.length === 0) {
			setSurveyCommentError('Comment is required for this action.')
			return
		}
		setSurveyCommentError('')
		if (selectedSurveyDetails?.surveyId) {
			setSubmiting(true)
			await completeSurvey(selectedSurveyDetails.surveyId, surveyComment, isMoreInfoRequired)
			await Promise.all([loadData(false), fetchSurveyDetail()])
			setSubmiting(false)
		}
	}

	return (
		<Paper
			sx={{
				p: 2,
				height: {
					xs: role.title === 'LineTech' ? 'calc(100vh - 230px)' : 'calc(100vh - 280px)',
					sm: role.title === 'LineTech' ? 'calc(100vh - 210px)' : 'calc(100vh - 260px)',
					md: role.title === 'LineTech' ? 'calc(100vh - 180px)' : 'calc(100vh - 230px)',
				},
				overflow: 'auto',
				backgroundColor: '#e8f4f8',
				'&::-webkit-scrollbar': {
					width: '8px',
				},
				'&::-webkit-scrollbar-track': {
					background: '#f1f1f1',
					borderRadius: '4px',
				},
				'&::-webkit-scrollbar-thumb': {
					background: '#888',
					borderRadius: '4px',
					'&:hover': {
						background: '#555',
					},
				},
			}}
		>
			<Typography variant='h6' sx={{ mb: 2, color: '#2c3e50' }}>
				TakeOff Sheet
			</Typography>

			{sheetLoading ? (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						height: 'calc(100% - 50px)',
						gap: 2,
					}}
				>
					<CircularProgress sx={{ color: '#2c3e50' }} />
					<Typography variant='h6' sx={{ color: '#95a5a6' }}>
						Loading survey details...
					</Typography>
				</Box>
			) : !selectedSurveyDetails ? (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: 'calc(100% - 50px)',
					}}
				>
					<Typography variant='h5' sx={{ color: '#95a5a6', textAlign: 'center' }}>
						Please select a survey to view details
					</Typography>
				</Box>
			) : (
				<>
					{missingAlert && (
						<Alert severity='warning'>
							This {missingAlert.workItemType} {missingAlert.workItemName} is split into{' '}
							<Link
								onClick={() => setSearchWorkItem(missingAlert.workItemName)}
								sx={{ cursor: 'pointer' }}
							>
								{missingAlert.splittedRequests + 1} requests.
							</Link>
						</Alert>
					)}
					<MemoizedSurveyBacklogList
						hierarchy={selectedSurveyDetails.surveyWorkItem}
						selectedSurveyId={selectedSurveyId}
						refreshData={fetchSurveyDetail}
						surveyCompleted={surveyCompleted}
					/>

					<Box sx={{ mt: 3, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
						<AssignmentIcon sx={{ color: '#e67e22', fontSize: 20 }} />
						<Typography variant='subtitle1' sx={{ color: '#2c3e50', fontWeight: 600 }}>
							Change Order Components
						</Typography>
						<Chip
							label={`${componentChangeSummaries.length} change${
								componentChangeSummaries.length !== 1 ? 's' : ''
							}`}
							size='small'
							sx={{
								backgroundColor: '#e67e22',
								color: 'white',
								fontWeight: 600,
								fontSize: '0.7rem',
								ml: 'auto',
							}}
						/>
					</Box>
					{componentChangeSummaries.length > 0 && (
						<Box
							sx={{
								backgroundColor: '#f8f9fa',
								borderRadius: 1.5,
								p: 1.5,
								border: '1px solid #ecf0f1',
							}}
						>
							{componentChangeSummaries.map((summary, index) => {
								const isIncrease = summary.proposedValue > summary.originalValue
								return (
									<Box
										key={`${summary.workItemPath}-${summary.componentType}-${index}`}
										sx={{
											display: 'flex',
											alignItems: 'center',
											p: 1,
											mb: index < componentChangeSummaries.length - 1 ? 1 : 0,
											borderRadius: 1,
											backgroundColor: 'white',
											border: `1px solid ${isIncrease ? '#27ae60' : '#e74c3c'}`,
											'&:hover': {
												backgroundColor: '#f8f9fa',
											},
										}}
									>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography variant='body2' sx={{ fontWeight: 600, color: '#2c3e50' }}>
												{summary.componentType}
											</Typography>

											<Typography variant='caption' sx={{ color: '#7f8c8d', display: 'block' }}>
												{summary.workItemPath}
											</Typography>
										</Box>

										<Box sx={{ textAlign: 'right', ml: 1 }}>
											<Typography
												variant='caption'
												sx={{ color: ' #2c3e50', display: 'block', fontWeight: 500 }}
											>
												{summary.originalValue.toLocaleString()} →{' '}
												{summary.proposedValue.toLocaleString()}
											</Typography>
											<Typography variant='caption' sx={{ color: '#7f8c8d' }}>
												{summary.unitOfMeasure}
											</Typography>
										</Box>
									</Box>
								)
							})}
						</Box>
					)}
					<Typography variant='h6' sx={{ mt: 3, mb: 1, color: '#2c3e50' }}>
						Survey Comments
					</Typography>
					<TextField
						fullWidth
						multiline
						rows={4}
						error={!!surveyCommentError}
						helperText={surveyCommentError}
						disabled={surveyCompleted}
						value={surveyComment}
						onChange={(e) => setSurveyComment(e.target.value)}
						placeholder='Enter survey comments here...'
						sx={{
							backgroundColor: 'white',
							'& .MuiOutlinedInput-root': {
								'&.Mui-focused fieldset': {
									borderColor: '#2c3e50',
								},
							},
						}}
					/>
					<Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
						<Button
							variant='contained'
							onClick={() => handleSubmit(false)}
							disabled={submiting || surveyCompleted}
							sx={{
								backgroundColor: '#2c3e50',
								'&:hover': {
									backgroundColor: '#1a252f',
								},
							}}
						>
							{submiting ? 'Submitting...' : 'Complete Survey'}
						</Button>
						<Button
							variant='contained'
							disabled={submiting || surveyCompleted}
							onClick={() => handleSubmit(true)}
							sx={{
								backgroundColor: '#e67e22',
								'&:hover': {
									backgroundColor: '#c0392b',
								},
							}}
						>
							More Info Required
						</Button>
					</Box>
				</>
			)}
		</Paper>
	)
}
